import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { teamLogo } from './poolFx';
import LineupEditor from './LineupEditor';
import { fmtTime } from '../../utils/time';

const logoImg = (abbrev, cls = 'ss-logo') => (
  <img className={cls} alt="" src={teamLogo(abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
);

// One game in detail: for each side, the players who scored since that side's previous game
// (points pile up between games), with the night, the line they were on and what it counted.
function GameDetail({ poolId, gameId, userId, lang }) {
  const fr = lang === 'fr';
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    setD(null);
    api.fetchSeasonGame(poolId, gameId).then((x) => { if (alive) setD(x); }).catch(() => { if (alive) setD({ error: true }); });
    return () => { alive = false; };
  }, [poolId, gameId]);
  if (!d) return <div className="ss-detail"><div className="ss-empty">{fr ? 'Chargement…' : 'Loading…'}</div></div>;
  if (d.error) return <div className="ss-detail"><div className="ss-empty">{fr ? 'Détail indisponible' : 'Detail not available'}</div></div>;
  const day = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' });
  const SLOT = (s) => (s === 'ACT' ? '' : /^[FD]X$|^GX$|^X$/.test(s) ? (fr ? 'réserve' : 'extra') : s === 'G1' ? (fr ? 'partant' : 'starter') : s?.[0] === 'D' ? `${fr ? 'paire' : 'pair'} ${s[1]}` : `${fr ? 'trio' : 'line'} ${s?.[1] ?? ''}`);
  // you on the left when it's your game
  const sides = d.away.user_id === userId ? [d.away, d.home] : [d.home, d.away];
  return (
    <div className="ss-detail">
      {sides.map((s) => (
        <div key={s.user_id} className={`ss-detail-side ${d.winner === s.user_id ? 'won' : ''}`}>
          <div className="ss-detail-head">
            {logoImg(s.team, 'ss-logo')}
            <span className="ss-name">
              <span className="ss-name-team">{s.team_name || s.team}</span>
              <small>{s.user_id === userId ? (fr ? 'Toi' : 'You') : s.is_cpu ? 'CPU' : s.username}{s.since ? ` · ${fr ? 'depuis le' : 'since'} ${day(s.since)}` : ''}</small>
            </span>
            <b className="ss-detail-total">{s.score ?? 0}</b>
          </div>
          {s.players.length === 0 && <div className="ss-detail-none">{fr ? 'Aucun point' : 'No points'}</div>}
          {s.players.map((p) => (
            <div key={p.player_id} className={`ss-detail-row ${p.points === 0 ? 'zero' : ''}`}>
              {logoImg(p.team, 'ss-logo sm')}
              <span className="ss-name">
                <span className="ss-name-team">{p.name}</span>
                <small>{p.lines.map((l) => `${day(l.date)}${SLOT(l.slot) ? ` · ${SLOT(l.slot)}` : ''} · ${l.raw}${l.mult !== 1 ? ` ×${l.mult}` : ''}`).join('  |  ')}</small>
              </span>
              <b className="ss-detail-pts">{p.points}</b>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// GM Season pools: the league table (all 32 teams), your next game, your results, the news.
// Every real NHL game = the member with the home team vs the member with the away team;
// score = the lineup points piled up since each side's previous game. Data from /api/pool/:id/season (server/season.js).
// standings = /api/pool/:id/standings (rosters + points), playersList = the Players ranking (from PoolLobby)
export default function SeasonView({ poolId, userId, lang = 'en', pool, standings, playersList, onOpenTrades }) {
  const fr = lang === 'fr';
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('table');   // table | players | games | news
  const [openTeam, setOpenTeam] = useState(null);   // league table: a team opened to see its lineup
  const [openGame, setOpenGame] = useState(null);   // a game opened to see who scored
  // News: what's new since the last time you opened the News tab on this device
  const seenKey = `seasonNewsSeen:${poolId}:${userId}`;
  const [seenAt, setSeenAt] = useState(() => { try { return Number(localStorage.getItem(seenKey)) || 0; } catch (e) { return 0; } });

  useEffect(() => {
    let alive = true;
    const load = () => api.fetchPoolSeason(poolId).then((d) => { if (alive && d && !d.error) setData(d); }).catch(() => {});
    load();
    const id = setInterval(load, 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [poolId]);

  const byUser = useMemo(() => Object.fromEntries((data?.members || []).map((m) => [m.user_id, m])), [data]);
  const who = (uid) => byUser[uid] || {};
  const teamName = (uid) => who(uid).team_name || who(uid).team || '—';
  const dayLabel = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { weekday: 'short', month: 'short', day: 'numeric' });

  // the news list: your results, the league's trades and signings, offers waiting for you
  const news = useMemo(() => {
    if (!data) return [];
    const items = [];
    (data.games || []).filter((g) => g.state === 'final' && (g.home.user_id === userId || g.away.user_id === userId)).forEach((g) => {
      const us = g.home.user_id === userId ? g.home : g.away;
      const them = g.home.user_id === userId ? g.away : g.home;
      items.push({ key: `g${g.game_id}`, ts: new Date(`${g.date}T23:59:00`).getTime(), type: 'result', won: g.winner === userId, us, them, game: g });
    });
    (data.moves || []).forEach((m, i) => items.push({ key: `m${i}${m.ts}`, ts: new Date(m.ts).getTime(), ...m }));
    return items.sort((a, b) => b.ts - a.ts);
  }, [data, userId]);
  const offers = data?.offersWaiting?.[userId] || 0;
  const fresh = news.filter((n) => n.ts > seenAt).length + offers;
  const openNews = () => {
    setTab('news');
    try { localStorage.setItem(seenKey, String(Date.now())); } catch (e) { /* private mode */ }
  };
  // leaving the News tab = everything in it has been seen
  useEffect(() => { if (tab !== 'news') { try { const v = Number(localStorage.getItem(seenKey)) || 0; if (v !== seenAt) setSeenAt(v); } catch (e) { /* */ } } }, [tab, seenKey, seenAt]);

  if (!data) return <div className="ss-empty">{fr ? 'Chargement…' : 'Loading…'}</div>;

  const mine = (g) => g.home.user_id === userId || g.away.user_id === userId;
  const myGames = (data.games || []).filter(mine);
  const live = myGames.filter((g) => g.state === 'live');
  const next = live[0] || (data.upcoming || []).find(mine);
  const results = myGames.filter((g) => g.state === 'final').reverse();
  const me = (data.table || []).find((r) => r.user_id === userId);
  const toggleGame = (id) => setOpenGame(openGame === id ? null : id);

  // one matchup card: you (left) vs them (right) — the score builds up between your games
  // (a plain function, not a <Component/>: defined in here it would re-mount on every refresh)
  const matchup = (g) => {
    const us = g.home.user_id === userId ? g.home : g.away;
    const them = g.home.user_id === userId ? g.away : g.home;
    const started = us.score != null;
    return (
      <div className={`ss-match ${g.state}`}>
        <span className="ss-match-when">
          {g.state === 'live' ? <span className="pl-live"><i />{fr ? 'Ce soir · en direct' : 'Tonight · live'}</span>
            : `${dayLabel(g.date)} · ${fmtTime(g.start, lang)}`}
        </span>
        <div className="ss-match-row">
          <span className="ss-side">
            {logoImg(us.team, 'ss-logo big')}
            <span className="ss-side-name">{fr ? 'Toi' : 'You'}<small>{who(us.user_id).team_name || us.team}</small></span>
          </span>
          <span className="ss-score">{started ? <>{us.score}<i>–</i>{them.score}</> : (g.home.user_id === userId ? 'vs' : '@')}</span>
          <span className="ss-side right">
            <span className="ss-side-name">{who(them.user_id).username}<small>{who(them.user_id).team_name || them.team}</small></span>
            {logoImg(them.team, 'ss-logo big')}
          </span>
        </div>
        <span className="ss-match-hint">{fr ? 'Tous les points de ton alignement depuis ton dernier match · le plus haut gagne' : 'All your lineup’s points since your last game · higher score wins'}</span>
        {started && (
          <button type="button" className="ss-more" onClick={() => toggleGame(g.game_id)}>
            {openGame === g.game_id ? (fr ? 'Fermer le détail' : 'Hide the detail') : (fr ? 'Qui a marqué?' : 'Who scored?')}
          </button>
        )}
      </div>
    );
  };

  if (data.status !== 'active' && data.status !== 'completed') {
    return <div className="ss-empty">{fr ? 'La saison commence après le repêchage.' : 'The season starts after the draft.'}</div>;
  }

  const when = (ts) => new Date(ts).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' });

  return (
    <div className="ss">
      <div className="ss-top">
        {me && (
          <div className="ss-record">
            <span className="ss-record-rank">#{me.rank}</span>
            <span className="ss-record-wl"><b>{me.w}</b>–<b>{me.l}</b></span>
            <small>{fr ? `${me.gp} matchs · ${me.pts} pts` : `${me.gp} games · ${me.pts} pts`}</small>
          </div>
        )}
        {next ? matchup(next) : <div className="ss-match ss-match-none">{fr ? 'Aucun match à venir cette semaine' : 'No game coming up this week'}</div>}
      </div>
      {next && openGame === next.game_id && <GameDetail poolId={poolId} gameId={next.game_id} userId={userId} lang={lang} />}

      <div className="pl-standings-tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'table'} className={tab === 'table' ? 'active' : ''} onClick={() => setTab('table')}>{fr ? 'Classement' : 'Standings'}</button>
        <button role="tab" aria-selected={tab === 'players'} className={tab === 'players' ? 'active' : ''} onClick={() => setTab('players')}>{fr ? 'Joueurs' : 'Players'}</button>
        <button role="tab" aria-selected={tab === 'games'} className={tab === 'games' ? 'active' : ''} onClick={() => setTab('games')}>{fr ? 'Mes matchs' : 'My games'}</button>
        <button role="tab" aria-selected={tab === 'news'} className={tab === 'news' ? 'active' : ''} onClick={openNews}>
          {fr ? 'Nouvelles' : 'News'}{fresh > 0 && tab !== 'news' && <span className="ss-badge">{fresh}</span>}
        </button>
      </div>

      {tab === 'table' && (
        <div className="ss-table">
          <div className="ss-row ss-head">
            <span className="ss-rank">#</span>
            <span />
            <span>{fr ? 'Équipe' : 'Team'}</span>
            <span className="ss-num ss-gp">{fr ? 'PJ' : 'GP'}</span>
            <span className="ss-num">{fr ? 'V' : 'W'}</span>
            <span className="ss-num">{fr ? 'D' : 'L'}</span>
            <span className="ss-num ss-pts">PTS</span>
            <span className="ss-last">{fr ? '5 derniers' : 'Last 5'}</span>
          </div>
          {(data.table || []).map((r) => (
            <React.Fragment key={r.user_id}>
            <div className={`ss-row ss-click ${r.user_id === userId ? 'me' : ''} ${openTeam === r.user_id ? 'open' : ''}`} onClick={() => setOpenTeam(openTeam === r.user_id ? null : r.user_id)}>
              <span className="ss-rank">{r.rank}</span>
              {logoImg(r.team)}
              <span className="ss-name">
                <span className="ss-name-team">{r.team_name || r.team}</span>
                <small>{r.is_cpu ? 'CPU' : r.username}{r.user_id === userId ? (fr ? ' · toi' : ' · you') : ''}</small>
              </span>
              <span className="ss-num ss-gp">{r.gp}</span>
              <span className="ss-num">{r.w}</span>
              <span className="ss-num">{r.l}</span>
              <span className="ss-num ss-pts">{r.pts}</span>
              <span className="ss-last">{r.last5.map((x, i) => <i key={i} className={x === 'W' ? 'w' : 'l'}>{fr ? (x === 'W' ? 'V' : 'D') : x}</i>)}</span>
            </div>
            {openTeam === r.user_id && pool && (
              <div className="ss-lineup">
                <LineupEditor pool={pool} userId={userId} lang={lang} readOnly={!(r.user_id === userId && pool.status === 'active')} ownerId={r.user_id} ownerName={r.username}
                  pointsById={(() => { const row = (standings?.standings || []).find((x) => x.user_id === r.user_id); return row ? Object.fromEntries((row.roster || []).map((p) => [p.player_id, p.points])) : {}; })()} />
              </div>
            )}
            </React.Fragment>
          ))}
        </div>
      )}

      {tab === 'players' && (playersList || <div className="ss-empty">{fr ? 'Chargement…' : 'Loading…'}</div>)}

      {tab === 'games' && (
        <div className="ss-table">
          {results.length === 0 && <div className="ss-empty">{fr ? 'Aucun match joué encore' : 'No games played yet'}</div>}
          {results.map((g) => {
            const us = g.home.user_id === userId ? g.home : g.away;
            const them = g.home.user_id === userId ? g.away : g.home;
            const won = g.winner === userId;
            return (
              <React.Fragment key={g.game_id}>
                <div className={`ss-row ss-game ss-click ${openGame === g.game_id ? 'open' : ''}`} onClick={() => toggleGame(g.game_id)}>
                  <span className="ss-date">{dayLabel(g.date)}</span>
                  {logoImg(them.team)}
                  <span className="ss-name">
                    <span className="ss-name-team">{g.home.user_id === userId ? 'vs' : '@'} {who(them.user_id).team_name || them.team}</span>
                    <small>{who(them.user_id).is_cpu ? 'CPU' : who(them.user_id).username}{g.tiebreak ? (fr ? ' · égalité, vrai gagnant' : ' · tie, real winner') : ''}</small>
                  </span>
                  <span className="ss-gscore">{us.score}–{them.score}</span>
                  <span className={`ss-result ${won ? 'w' : 'l'}`}>{won ? (fr ? 'V' : 'W') : (fr ? 'D' : 'L')}</span>
                </div>
                {openGame === g.game_id && <GameDetail poolId={poolId} gameId={g.game_id} userId={userId} lang={lang} />}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {tab === 'news' && (
        <div className="ss-table ss-news">
          {offers > 0 && (
            <button type="button" className="ss-news-row ss-news-offer" onClick={() => onOpenTrades?.()}>
              <span className="ss-news-tag offer">{fr ? 'Offre' : 'Offer'}</span>
              <span className="ss-news-text"><b>{fr ? `${offers} offre${offers > 1 ? 's' : ''} d’échange t’attend${offers > 1 ? 'ent' : ''}` : `${offers} trade offer${offers > 1 ? 's' : ''} waiting for you`}</b><small>{fr ? 'Ouvre Échanges › Offres' : 'Open Trades › Offers'}</small></span>
            </button>
          )}
          {news.length === 0 && offers === 0 && <div className="ss-empty">{fr ? 'Rien de neuf pour l’instant' : 'Nothing new yet'}</div>}
          {news.map((n) => {
            const isNew = n.ts > seenAt;
            if (n.type === 'result') {
              return (
                <button type="button" key={n.key} className={`ss-news-row ${isNew ? 'new' : ''}`} onClick={() => { setTab('games'); setOpenGame(n.game.game_id); }}>
                  <span className={`ss-news-tag ${n.won ? 'win' : 'loss'}`}>{n.won ? (fr ? 'Victoire' : 'Win') : (fr ? 'Défaite' : 'Loss')}</span>
                  {logoImg(n.them.team, 'ss-logo sm')}
                  <span className="ss-news-text">
                    <b>{n.won ? (fr ? `Tu as battu ${teamName(n.them.user_id)}` : `You beat ${teamName(n.them.user_id)}`) : (fr ? `Tu as perdu contre ${teamName(n.them.user_id)}` : `You lost to ${teamName(n.them.user_id)}`)} {n.us.score}–{n.them.score}</b>
                    <small>{dayLabel(n.game.date)}{n.game.tiebreak ? (fr ? ' · égalité, décidée par le vrai match' : ' · tie, decided by the real game') : ''}</small>
                  </span>
                </button>
              );
            }
            if (n.type === 'trade') {
              const mineTrade = n.a === userId || n.b === userId;
              return (
                <div key={n.key} className={`ss-news-row ${isNew ? 'new' : ''}`}>
                  <span className="ss-news-tag trade">{fr ? 'Échange' : 'Trade'}</span>
                  {logoImg(who(n.a).team, 'ss-logo sm')}
                  <span className="ss-news-text">
                    <b>{teamName(n.a)} ⇄ {teamName(n.b)}{mineTrade ? (fr ? ' (toi)' : ' (you)') : ''}</b>
                    <small>{n.give.join(', ')} {fr ? 'contre' : 'for'} {n.get.join(', ')} · {when(n.ts)}</small>
                  </span>
                </div>
              );
            }
            return (
              <div key={n.key} className={`ss-news-row ${isNew ? 'new' : ''}`}>
                <span className="ss-news-tag signing">{fr ? 'Signature' : 'Signing'}</span>
                {logoImg(who(n.a).team, 'ss-logo sm')}
                <span className="ss-news-text">
                  <b>{teamName(n.a)} {fr ? 'signe' : 'sign'} {n.add}</b>
                  <small>{n.drop ? `${fr ? 'libère' : 'drop'} ${n.drop} · ` : ''}{when(n.ts)}</small>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
